import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import * as icons from "react-bootstrap-icons";
import { Loader } from "rsuite";
import TopNavbar from "../../CommonComponent/topNavbar/TopNavbar";
import Sidebar from "../../CommonComponent/sidebar/Sidebar";
import { Footer } from "../../Footer";
import { useLotCountdown } from "../../hooks/useLotCountdown";
import { detailsPath } from "../../utils/helper/auctionList";
import {
  TEAM_STATES,
  poolOf,
  statsOf,
  teamGroups,
  teamState,
} from "../../utils/helper/auctionDetails";
import { AuctionPassDialog } from "./AuctionPassDialog";
import { AuctionBidArea } from "./AuctionBidArea.jsx";
import {
  AuctionHeader,
  AuctionWinnings,
  RecentBids,
  SelectedTeam,
  StatsRow,
  TeamList,
} from "./AuctionDetailsParts.jsx";
import { useAuctionDetails } from "./useAuctionDetails";
import { useLotWinnings } from "./useLotWinnings";
import { useRecentBids } from "./useRecentBids";

/** Where "Auctions" leads: the list's Live and Upcoming tab, with its search and filter at their defaults (AC17). */
const AUCTIONS_PATH = "/Player/AuctionPlayer";

const readUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("isUser"))?.userId;
  } catch {
    return undefined;
  }
};

const AuctionDetailsPage = ({ auctionId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(false);
  const [query, setQuery] = useState("");
  // The auction the player is unlocking (single-auction access); null while the dialog is closed.
  const [passRow, setPassRow] = useState(null);
  // #238 AC14: the outbid pop-up's Place Bid - select the team in Bidding now and fill in its Next
  // Minimum Bid. { arrival } until the auction has loaded, then also { teamId } once applied.
  const [prefill, setPrefill] = useState(null);
  const page = useAuctionDetails(auctionId);
  const userId = readUserId();

  const { details, lots, auctionStatus, runningLot, runningClock } = page;
  const running = useLotCountdown(runningClock?.endsAt ?? null, runningClock?.frozenSeconds ?? null);
  const startsIn = useLotCountdown(auctionStatus === "Upcoming" ? page.startsAt : null, null);
  const pool = poolOf(lots);
  const winnings = useLotWinnings({
    auctionId,
    loadedRow: details,
    totalPool: pool,
    ended: auctionStatus === "Closed" || auctionStatus === "Archive",
  });

  const auctionStarted = auctionStatus !== null && auctionStatus !== "Upcoming";
  const selected = lots.find((lot) => lot.teamId === page.selectedId) ?? null;
  const nextTeamId = lots.find((lot) => teamState(lot) === TEAM_STATES.UPCOMING)?.teamId ?? null;
  const runningTeamId = runningLot?.teamId ?? null;
  const recent = useRecentBids(auctionId, selected);
  const { select } = page;

  // Taken once per arrival, as UserSuper takes its targetTab, and cleared from the history entry so
  // Back or a reload does not fill the amount in again. Also on this auction's own page, which stays
  // mounted: the pop-up can be pressed here too.
  useEffect(() => {
    if (!location.state?.bidPrefill) return;
    setPrefill({ arrival: location.key });
    navigate(location.pathname + location.search, { replace: true, state: null });
    // Only a new arrival may apply it: the clearing navigate above is itself a location change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Applied once the auction has loaded: the team in Bidding now, whatever is selected. With no team
  // running any more the page just opens.
  useEffect(() => {
    if (!prefill || prefill.teamId !== undefined || !details) return;
    if (runningTeamId !== null) select(runningTeamId);
    setPrefill({ ...prefill, teamId: runningTeamId });
  }, [prefill, details, runningTeamId, select]);

  // Selecting another team ends it, so coming back does not fill the amount in again.
  useEffect(() => {
    if (prefill?.teamId !== undefined && page.selectedId !== prefill.teamId) setPrefill(null);
  }, [prefill, page.selectedId]);

  const prefilled = prefill?.teamId !== undefined && prefill.teamId !== null && prefill.teamId === selected?.teamId;

  let content;
  if (!details && page.loading) {
    content = (
      <div className="auction-details-loading">
        <Loader />
      </div>
    );
  } else if (page.notFound) {
    content = <div className="auction-details-message">This auction is not available.</div>;
  } else if (!details) {
    content = <div className="auction-details-message">This auction could not be loaded. Please try again.</div>;
  } else {
    content = (
      <div className="auction-details">
        <TeamList
          lots={lots}
          groups={teamGroups(lots, { query, auctionStarted })}
          query={query}
          onQuery={setQuery}
          selectedId={page.selectedId}
          onSelect={page.select}
          runningTeamId={runningTeamId}
          seconds={running.seconds}
          nextTeamId={nextTeamId}
          userId={userId}
        />
        <section className="auction-details-panel" aria-label="Auction details">
          <AuctionHeader details={details} status={auctionStatus} startsInSeconds={startsIn.seconds} />
          <StatsRow stats={statsOf(details, lots)} />
          {selected && (
            <SelectedTeam
              lot={selected}
              teamCount={lots.length}
              sportName={details.sportName}
              secondaryMarket={details.secondaryMarket}
              running={selected.teamId === runningTeamId}
              seconds={running.seconds}
              userId={userId}
            />
          )}
          <div className="auction-details-sec auction-details-bid-area">
            {selected && (
              <AuctionBidArea
                key={prefilled ? `${selected.teamId}:${prefill.arrival}` : selected.teamId}
                lot={selected}
                details={details}
                auctionStarted={auctionStarted}
                userId={userId}
                secondsLeft={selected.teamId === runningTeamId ? running.seconds : 0}
                initialAmount={prefilled ? selected.minNextBid : undefined}
                onUnlock={() => setPassRow({ auctionId: details.auctionId, auctionName: details.auctionName })}
                onReload={page.reload}
              />
            )}
          </div>
          <div className="auction-details-bottom">
            {selected ? <RecentBids lot={selected} recent={recent} /> : <div className="auction-details-recent" />}
            <AuctionWinnings pool={pool} winnings={winnings} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <TopNavbar sidebarfun={() => setData(!data)} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div className={data ? "dashboard-content full-contnet" : "dashboard-content"}>
          <Link to={AUCTIONS_PATH} className="page-back">
            <icons.ArrowLeft aria-hidden="true" />
            Auctions
          </Link>
          <h1 className="heading-dash page-heading">Auction Details</h1>
          {content}
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>
      <AuctionPassDialog row={passRow} onClose={() => setPassRow(null)} onRedeemed={page.reload} />
    </>
  );
};

/**
 * The player's Auction Details page (#237, PL-019.2, mockup v7 States 4-13): every team of one
 * auction on the left, grouped Bidding now / Up next / Sold; on the right the auction, its stats, the
 * selected team, the bid area (#238, PL-019.3), and Recent Bids (#239, PL-019.4) next to Auction
 * Winnings. The outbid pop-up's Place Bid opens it with `{ bidPrefill: true }` in the router state.
 *
 * Opened from a Live or Upcoming row of the Auctions list, or its Place Bid, at
 * /Player/AuctionDetails/:auctionId - also by a player who has not unlocked the auction, who sees it
 * all and the Unlock prompt in place of the bidding controls. Everything follows the server live
 * (useAuctionDetails); the page stays open when the auction ends (AC16). Keyed on the auction, so
 * another auction starts from nothing.
 */
export const AuctionDetails = () => {
  const { auctionId } = useParams();
  return <AuctionDetailsPage key={auctionId} auctionId={auctionId} />;
};

/**
 * The old Place Bid page's address (#237). A history entry or bookmark from before carries the
 * auction in its router state, and opens that auction's details; without it, the Auctions list.
 */
export const PlaceBidRedirect = () => {
  const { state } = useLocation();
  const auctionId = Number(state?.auctionId);
  return (
    <Navigate replace to={Number.isInteger(auctionId) && auctionId > 0 ? detailsPath(auctionId) : AUCTIONS_PATH} />
  );
};
